/**
 * AMAE – richiesta fattura.
 *
 * AMAE Board Games e' in regime forfettario: la fattura si emette solo su
 * richiesta, e la richiesta deve arrivare prima del pagamento. Nel riepilogo
 * del carrello (pagina e cassetto) lo snippet amae-fattura mette la casella
 * "Mi serve la fattura" e i campi dei dati. Questo script:
 *
 *   - SALVA i dati come attributi del carrello con /cart/update.js quando il
 *     cliente esce da un campo (evento change, non a ogni tasto) o cambia la
 *     casella; togliendo la spunta azzera tutti gli attributi. Shopify li
 *     riporta nell'ordine, sotto "Dettagli aggiuntivi". I salvataggi vanno
 *     in fila, cosi' un azzeramento non viene superato da un salvataggio
 *     partito prima;
 *   - tiene UGUALI le copie del modulo: pagina carrello e cassetto possono
 *     stare nella stessa pagina. Quando Horizon ridisegna il riepilogo, i
 *     valori arrivano dagli attributi salvati (li scrive lo snippet) e i
 *     campi, con data-skip-node-update, restano come sono;
 *   - al PAGAMENTO, se la casella e' selezionata, controlla i dati: se
 *     mancano o sono sbagliati ferma il clic, scrive il messaggio accanto a
 *     ogni campo da correggere (role="alert") e porta il focus sul primo; se
 *     vanno bene, salva tutto, aspetta che il salvataggio sia finito e solo
 *     allora lascia partire il pagamento.
 *
 * I pulsanti di pagamento accelerato li nasconde il CSS dello snippet.
 *
 * Come per "una versione per ordine", clic e invii si ascoltano sulla
 * finestra, in fase di cattura: arrivano prima di Horizon, che ascolta sul
 * documento.
 */
(() => {
  if (window.__amaeFattura) return;
  window.__amaeFattura = true;

  const SI = 'Sì';

  /** I nomi degli attributi: gli stessi dello snippet amae-fattura. */
  const ATTRIBUTI = {
    richiesta: 'Fattura richiesta',
    intestatario: 'Fattura - Intestatario',
    codiceFiscale: 'Fattura - Codice fiscale',
    partitaIva: 'Fattura - Partita IVA',
    destinatario: 'Fattura - Codice destinatario o PEC',
  };

  /** I campi, nell'ordine in cui compaiono. */
  const CAMPI = ['intestatario', 'codiceFiscale', 'partitaIva', 'destinatario'];

  const MESSAGGI = {
    intestatario: 'Scrivi nome e cognome, oppure la ragione sociale.',
    unCodice: 'Indica il codice fiscale oppure la partita IVA.',
    codiceFiscale: 'Il codice fiscale ha 16 caratteri, lettere e numeri, oppure 11 cifre.',
    partitaIva:
      'La partita IVA italiana ha 11 cifre. Per una partita IVA estera scrivi prima la sigla del paese, per esempio DE123456789.',
    destinatarioMancante:
      'Per le attività italiane serve il codice destinatario (7 caratteri) oppure l’indirizzo PEC.',
    destinatario: 'Il codice destinatario ha 7 caratteri, lettere e numeri. In alternativa scrivi un indirizzo PEC valido.',
    salvataggio: 'Non siamo riusciti a salvare i dati della fattura. Controlla la connessione e riprova.',
  };

  /* ---------- I dati ---------- */

  /**
   * Spazi via, maiuscole automatiche. Il nome tiene gli spazi fra le parole;
   * il codice destinatario resta in minuscolo se e' un indirizzo PEC.
   * @param {string} campo
   * @param {unknown} valore
   */
  const normalizza = (campo, valore) => {
    const testo = String(valore ?? '');
    if (campo === 'intestatario') return testo.replace(/\s+/g, ' ').trim();
    const compatto = testo.replace(/\s+/g, '');
    if (campo === 'destinatario' && compatto.includes('@')) return compatto.toLowerCase();
    return compatto.toUpperCase();
  };

  const FORMATI = {
    codiceFiscale: /^(?:[A-Z0-9]{16}|\d{11})$/,
    partitaIvaItaliana: /^(?:IT)?\d{11}$/,
    partitaIvaEstera: /^[A-Z]{2}[A-Z0-9]{2,13}$/,
    codiceDestinatario: /^[A-Z0-9]{7}$/,
    pec: /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/,
  };

  /** Italiana: 11 cifre o prefisso IT (e allora servono 11 cifre dopo). */
  const partitaIvaItaliana = (partitaIva) => /^\d{11}$/.test(partitaIva) || partitaIva.startsWith('IT');

  /**
   * I campi da correggere, nell'ordine dei campi.
   * @param {Record<string, string>} dati - gia' normalizzati
   * @returns {Array<[string, string]>} coppie [campo, messaggio]
   */
  const errori = (dati) => {
    const lista = [];
    if (!dati.intestatario) lista.push(['intestatario', MESSAGGI.intestatario]);

    if (!dati.codiceFiscale && !dati.partitaIva) {
      lista.push(['codiceFiscale', MESSAGGI.unCodice]);
    } else if (dati.codiceFiscale && !FORMATI.codiceFiscale.test(dati.codiceFiscale)) {
      lista.push(['codiceFiscale', MESSAGGI.codiceFiscale]);
    }

    const italiana = Boolean(dati.partitaIva) && partitaIvaItaliana(dati.partitaIva);
    if (dati.partitaIva) {
      const valida = italiana
        ? FORMATI.partitaIvaItaliana.test(dati.partitaIva)
        : FORMATI.partitaIvaEstera.test(dati.partitaIva);
      if (!valida) lista.push(['partitaIva', MESSAGGI.partitaIva]);
    }

    if (dati.destinatario) {
      const valido = FORMATI.codiceDestinatario.test(dati.destinatario) || FORMATI.pec.test(dati.destinatario);
      if (!valido) lista.push(['destinatario', MESSAGGI.destinatario]);
    } else if (italiana) {
      lista.push(['destinatario', MESSAGGI.destinatarioMancante]);
    }

    return lista;
  };

  /* ---------- Il modulo nella pagina ---------- */

  /** @returns {HTMLElement[]} tutte le copie del modulo */
  const moduli = () => [...document.querySelectorAll('[data-amae-fattura]')];

  /** @param {HTMLElement} modulo @returns {HTMLInputElement | null} */
  const casella = (modulo) => modulo.querySelector('[data-amae-fattura-casella]');

  /** @param {HTMLElement} modulo @param {string} nome @returns {HTMLInputElement | null} */
  const campo = (modulo, nome) => modulo.querySelector(`[data-amae-fattura-campo="${nome}"]`);

  /** @param {HTMLElement} modulo */
  const leggi = (modulo) => Object.fromEntries(CAMPI.map((nome) => [nome, normalizza(nome, campo(modulo, nome)?.value)]));

  /**
   * Gli attributi da mandare al carrello: tutti, gia' riempiti, se la
   * fattura e' richiesta; tutti vuoti (cioe' tolti) se non lo e'.
   * @param {boolean} richiesta
   * @param {Record<string, string>} [dati]
   */
  const attributi = (richiesta, dati = {}) =>
    richiesta
      ? { [ATTRIBUTI.richiesta]: SI, ...Object.fromEntries(CAMPI.map((nome) => [ATTRIBUTI[nome], dati[nome] ?? ''])) }
      : Object.fromEntries(Object.values(ATTRIBUTI).map((nome) => [nome, '']));

  /**
   * Porta la casella e i valori di una copia del modulo in tutte le altre.
   * @param {HTMLElement} origine
   */
  const allinea = (origine) => {
    const spunta = Boolean(casella(origine)?.checked);
    const dati = leggi(origine);
    for (const modulo of moduli()) {
      if (modulo === origine) continue;
      const altra = casella(modulo);
      if (altra) altra.checked = spunta;
      for (const nome of CAMPI) {
        const input = campo(modulo, nome);
        if (input && input !== document.activeElement) input.value = dati[nome];
      }
      if (!spunta) togliErrori(modulo);
    }
  };

  /* ---------- I messaggi ---------- */

  /** @param {HTMLInputElement} input */
  const idErrore = (input) => `${input.id}-errore`;

  /** @param {HTMLInputElement} input */
  const descrizioni = (input) => (input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);

  /** @param {HTMLElement} modulo @param {string} id */
  const trova = (modulo, id) => modulo.querySelector(`#${CSS.escape(id)}`);

  /**
   * Il messaggio sotto il campo, annunciato ai lettori di schermo, e il
   * campo segnato come da correggere.
   * @param {HTMLElement} modulo @param {string} nome @param {string} testo
   */
  const mettiErrore = (modulo, nome, testo) => {
    const input = campo(modulo, nome);
    if (!input) return;
    const id = idErrore(input);
    let messaggio = trova(modulo, id);
    if (!messaggio) {
      messaggio = document.createElement('p');
      messaggio.id = id;
      messaggio.className = 'amae-fattura__errore';
      messaggio.setAttribute('role', 'alert');
      input.after(messaggio);
    }
    if (messaggio.textContent !== testo) messaggio.textContent = testo;
    input.setAttribute('aria-invalid', 'true');
    const lista = descrizioni(input);
    if (!lista.includes(id)) input.setAttribute('aria-describedby', [...lista, id].join(' '));
  };

  /** @param {HTMLElement} modulo @param {string} nome */
  const togliErrore = (modulo, nome) => {
    const input = campo(modulo, nome);
    if (!input) return;
    const id = idErrore(input);
    trova(modulo, id)?.remove();
    input.removeAttribute('aria-invalid');
    const lista = descrizioni(input).filter((voce) => voce !== id);
    if (lista.length) input.setAttribute('aria-describedby', lista.join(' '));
    else input.removeAttribute('aria-describedby');
  };

  /** @param {HTMLElement} modulo */
  const togliErrori = (modulo) => {
    for (const nome of CAMPI) togliErrore(modulo, nome);
    modulo.querySelector('.amae-fattura__errore--salvataggio')?.remove();
  };

  /**
   * Il messaggio quando il salvataggio non riesce: sotto la casella.
   * @param {HTMLElement} modulo
   */
  const erroreSalvataggio = (modulo) => {
    modulo.querySelector('.amae-fattura__errore--salvataggio')?.remove();
    const messaggio = document.createElement('p');
    messaggio.className = 'amae-fattura__errore amae-fattura__errore--salvataggio';
    messaggio.setAttribute('role', 'alert');
    messaggio.textContent = MESSAGGI.salvataggio;
    (modulo.querySelector('.amae-fattura__scelta') ?? modulo.firstElementChild)?.after(messaggio);
  };

  /**
   * Horizon, ridisegnando il riepilogo, toglie i messaggi (non sono nel
   * markup del server) ma lascia i campi com'erano, compreso aria-invalid.
   * Qui si tolgono i segni rimasti senza messaggio.
   */
  const pulisciOrfani = () => {
    for (const modulo of moduli()) {
      for (const nome of CAMPI) {
        const input = campo(modulo, nome);
        if (input?.getAttribute('aria-invalid') === 'true' && !trova(modulo, idErrore(input))) togliErrore(modulo, nome);
      }
    }
  };

  /* ---------- Il salvataggio ---------- */

  /** L'ultimo salvataggio in fila: si risolve con true se e' andato bene. */
  let coda = Promise.resolve(true);
  let inCorso = 0;
  /** Un azzeramento non riuscito: va ripetuto prima del pagamento. */
  let daAzzerare = false;

  /**
   * @param {Record<string, string>} valori - nome attributo -> valore ('' lo toglie)
   * @returns {Promise<boolean>}
   */
  const salva = (valori) => {
    inCorso += 1;
    const url = `${window.Theme?.routes?.cart_update_url || '/cart/update'}.js`;
    const azzeramento = Object.values(valori).every((valore) => valore === '');
    const giro = coda
      .then(() =>
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: valori }),
        })
      )
      .then((risposta) => {
        if (!risposta.ok) throw new Error(`Carrello non aggiornato (${risposta.status})`);
        if (azzeramento) daAzzerare = false;
        return true;
      })
      .catch(() => {
        if (azzeramento) daAzzerare = true;
        return false;
      })
      .finally(() => {
        inCorso -= 1;
      });
    coda = giro;
    return giro;
  };

  /* ---------- Casella e campi ---------- */

  document.addEventListener('change', (evento) => {
    const bersaglio = evento.target;
    if (!(bersaglio instanceof HTMLInputElement)) return;
    const modulo = /** @type {HTMLElement | null} */ (bersaglio.closest('[data-amae-fattura]'));
    if (!modulo) return;

    if (bersaglio.matches('[data-amae-fattura-casella]')) {
      if (!bersaglio.checked) togliErrori(modulo);
      allinea(modulo);
      salva(attributi(bersaglio.checked, leggi(modulo)));
      return;
    }

    const nome = bersaglio.dataset.amaeFatturaCampo;
    if (!nome || !CAMPI.includes(nome)) return;
    bersaglio.value = normalizza(nome, bersaglio.value);
    allinea(modulo);
    if (!casella(modulo)?.checked) return;
    salva({ [ATTRIBUTI[nome]]: bersaglio.value });

    // I messaggi gia' mostrati si aggiornano subito (spariscono se il dato
    // ora va bene); quelli nuovi aspettano il clic sul pagamento.
    pulisciOrfani();
    const daCorreggere = new Map(errori(leggi(modulo)));
    for (const altro of CAMPI) {
      const input = campo(modulo, altro);
      if (!input || !trova(modulo, idErrore(input))) continue;
      const testo = daCorreggere.get(altro);
      if (testo) mettiErrore(modulo, altro, testo);
      else togliErrore(modulo, altro);
    }
  });

  document.addEventListener('focusin', (evento) => {
    if (evento.target instanceof Element && evento.target.closest('[data-amae-fattura]')) pulisciOrfani();
  });

  /* ---------- Il pagamento ---------- */

  /** Il pulsante a cui e' appena stato dato il via: il secondo giro passa. */
  let lasciaPassare = null;

  /** @param {Element} bottone */
  const moduloDi = (bottone) =>
    /** @type {HTMLElement | null} */ (bottone.closest('.cart__ctas')?.querySelector('[data-amae-fattura]') ?? null);

  /**
   * @param {Event} evento
   * @param {HTMLElement} bottone - il pulsante di pagamento
   * @param {() => void} procedi - ripete il clic (o l'invio) lasciandolo passare
   */
  const alPagamento = (evento, bottone, procedi) => {
    if (lasciaPassare === bottone) return;
    const modulo = moduloDi(bottone);
    const richiesta = Boolean(modulo && casella(modulo)?.checked);
    if (!richiesta && inCorso === 0 && !daAzzerare) return;

    evento.preventDefault();
    evento.stopImmediatePropagation();

    /** @type {Promise<boolean>} */
    let attesa = coda;
    if (richiesta && modulo) {
      pulisciOrfani();
      togliErrori(modulo);
      const dati = leggi(modulo);
      const daCorreggere = errori(dati);
      if (daCorreggere.length) {
        for (const [nome, testo] of daCorreggere) mettiErrore(modulo, nome, testo);
        campo(modulo, daCorreggere[0][0])?.focus();
        return;
      }
      attesa = salva(attributi(true, dati));
    } else if (daAzzerare) {
      attesa = salva(attributi(false));
    }

    attesa.then((riuscito) => {
      if (!riuscito) {
        if (modulo) erroreSalvataggio(modulo);
        return;
      }
      lasciaPassare = bottone;
      try {
        procedi();
      } finally {
        lasciaPassare = null;
      }
    });
  };

  // 1. Il clic sul pulsante di pagamento.
  window.addEventListener(
    'click',
    (evento) => {
      const bersaglio = evento.target instanceof Element ? evento.target : null;
      const bottone = /** @type {HTMLButtonElement | null} */ (bersaglio?.closest('button[name="checkout"], input[name="checkout"]'));
      if (!bottone || bottone.disabled) return;
      alPagamento(evento, bottone, () => bottone.click());
    },
    true
  );

  // 2. L'invio del carrello verso il pagamento senza clic (per esempio Invio
  //    in un campo della quantita').
  window.addEventListener(
    'submit',
    (evento) => {
      const form = evento.target;
      const bottone = /** @type {SubmitEvent} */ (evento).submitter;
      if (!(form instanceof HTMLFormElement) || !(bottone instanceof HTMLElement)) return;
      if (bottone.getAttribute('name') !== 'checkout') return;
      alPagamento(evento, bottone, () => form.requestSubmit(bottone));
    },
    true
  );
})();
